/**
 * DQ-453: the claim form's "Additional Details" card (FIR status, vehicle
 * location, third-party loss) has no column of its own on the claim, so it is
 * stored in `claim_data`, which the create API already accepts and returns.
 */

export const FIR_STATUS_LABELS: Record<string, string> = {
  not_filed: 'Not Filed',
  filed: 'Filed',
  not_required: 'Not Required',
};

const EXTRA_KEYS = ['fir_status', 'vehicle_location', 'third_party_loss'] as const;

/**
 * Moves the three form-only inputs out of `payload` into `claim_data` (blank
 * ones are left out) and, when the FIR is filed, sets `police_report_filed`,
 * the flag that drives the document checklist.
 */
export function packClaimExtras(payload: Record<string, unknown>): Record<string, unknown> {
  const { fir_status, vehicle_location, third_party_loss, ...rest } = payload;
  const values: Record<string, unknown> = { fir_status, vehicle_location, third_party_loss };
  const claimData: Record<string, string> = {};
  for (const key of EXTRA_KEYS) {
    const value = values[key];
    if (typeof value === 'string' && value.trim()) claimData[key] = value.trim();
  }
  if (Object.keys(claimData).length > 0) rest.claim_data = claimData;
  if (claimData.fir_status === 'filed') rest.police_report_filed = true;
  return rest;
}

/**
 * #633: `claim_data` for an edit. Starts from what is stored (keys this form
 * does not show survive), then sets each extra to the edited value or removes
 * it when it was cleared.
 */
export function mergeClaimExtras(
  existing: unknown,
  values: { fir_status?: string; vehicle_location?: string; third_party_loss?: string }
): Record<string, unknown> {
  const merged: Record<string, unknown> =
    existing && typeof existing === 'object' ? { ...(existing as Record<string, unknown>) } : {};
  for (const key of EXTRA_KEYS) {
    const value = values[key];
    if (typeof value === 'string' && value.trim()) merged[key] = value.trim();
    else delete merged[key];
  }
  return merged;
}

/** The saved extras as label/value pairs for the detail page; empty ones are dropped. */
export function claimExtrasRows(claimData: unknown): { label: string; value: string }[] {
  if (!claimData || typeof claimData !== 'object') return [];
  const data = claimData as Record<string, unknown>;
  const text = (key: string) => (typeof data[key] === 'string' ? (data[key] as string).trim() : '');
  const rows = [
    { label: 'FIR Status', value: FIR_STATUS_LABELS[text('fir_status')] ?? text('fir_status') },
    { label: 'Vehicle Location', value: text('vehicle_location') },
    { label: 'Third Party Loss', value: text('third_party_loss') },
  ];
  return rows.filter((row) => row.value);
}
